import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { font } from "../../constants/font";
import {
  buildExecutionTraceTimeline,
  getSequenceBlockSummary,
} from "./execution-trace-organizer";

const BREAKDOWN_SCORE = {
  "hook_strength": 25,
  "reframe_insight": 20,
  "emotional_resonance": 18,
  "standalone_clarity": 17,
  "quotability": 12,
  "clean_ending": 8
};

const Panel = styled.div`
  width: ${(props) => (props.isDrawer ? "100%" : "520px")};
  height: ${(props) => (props.isDrawer ? "100vh" : "calc(100vh - 104px)")};
  max-width: 100%;
  background: #fafbfc;
  border-left: ${(props) => (props.isDrawer ? `1px solid ${props.theme.resizeBorder}` : "none")};
  border-right: ${(props) => (props.isDrawer ? "none" : `1px solid ${props.theme.resizeBorder}`)};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media screen and (max-width: 1200px) {
    width: ${(props) => (props.isDrawer ? "100%" : "420px")};
  }

  @media screen and (max-width: 767px) {
    width: 100%;
    height: ${(props) => (props.isDrawer ? "100vh" : "40%")};
    border-right: none;
    border-bottom: ${(props) => (props.isDrawer ? "none" : `1px solid ${props.theme.resizeBorder}`)};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: 3px solid #5568d3;
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderTitle = styled.div`
  font: ${font.bold_12};
  font-size: 15px;
  color: white;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const ProjectTitle = styled.div`
  font: ${font.normalMicro_16};
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 4px;
`;

const QuickNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 16px;
  background: #edf2f7;
  border-bottom: 1px solid #e2e8f0;
  max-height: 88px;
  overflow-y: auto;
`;

const QuickNavLabel = styled.span`
  font: ${font.bold_12};
  font-size: 11px;
  color: #4a5568;
  width: 100%;
`;

const TraceToolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const ToolbarButton = styled.button`
  border: 1px solid #cbd5e0;
  background: white;
  color: #4a5568;
  font: ${font.bold_12};
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    border-color: #667eea;
    color: #667eea;
  }
`;

const QuickNavPill = styled.button`
  border: 1px solid #cbd5e0;
  background: white;
  color: #2d3748;
  font: ${font.bold_12};
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #667eea;
    color: #667eea;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;

    &:hover {
      background: #a0aec0;
    }
  }
`;

const EventCard = styled.div`
  background: white;
  border: 1px solid ${(props) => (props.isExpanded ? "#cbd5e0" : "#e2e8f0")};
  border-radius: 10px;
  margin-bottom: 8px;
  box-shadow: ${(props) => (props.isExpanded
    ? "0 4px 12px rgba(0, 0, 0, 0.08)"
    : "0 1px 2px rgba(0, 0, 0, 0.04)")};
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EventCardHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: ${(props) => (props.isExpanded ? "#f8fafc" : "white")};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: #f7fafc;
  }
`;

const EventCardBody = styled.div`
  padding: 0 14px 14px;
  border-top: 1px solid #edf2f7;
`;

const EventHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const CardChevron = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  font-size: 10px;
  color: #a0aec0;
  transition: transform 0.2s;
  transform: ${(props) => (props.isExpanded ? "rotate(90deg)" : "rotate(0)")};
`;

const CollapsedTitle = styled.span`
  flex: 1;
  min-width: 0;
  font: ${font.normalMicro_16};
  font-size: 13px;
  color: #4a5568;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  flex-shrink: 0;
  background: ${(props) => {
    if (props.soft) {
      if (props.variant === "agent") return "#fce7f3";
      if (props.variant === "iteration") return "#d1fae5";
      if (props.variant === "reasoning") return "#e0f2fe";
      if (props.variant === "initialize") return "#e0e7ff";
      if (props.variant === "summary") return "#ffedd5";
      return "#edf2f7";
    }
    if (props.variant === "agent") return "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)";
    if (props.variant === "iteration") return "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)";
    if (props.variant === "reasoning") return "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)";
    if (props.variant === "initialize") return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
    if (props.variant === "summary") return "linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)";
    return "#e2e8f0";
  }};
  color: ${(props) => {
    if (props.soft) {
      if (props.variant === "agent") return "#9d174d";
      if (props.variant === "iteration") return "#047857";
      if (props.variant === "reasoning") return "#0369a1";
      if (props.variant === "initialize") return "#4338ca";
      if (props.variant === "summary") return "#c2410c";
      return "#4a5568";
    }
    return props.variant ? "white" : "#4a5568";
  }};
  font: ${font.bold_12};
  font-size: 10px;
  border-radius: 6px;
  text-transform: ${(props) => (props.variant ? "lowercase" : "none")};
  letter-spacing: 0.3px;
  white-space: nowrap;
  box-shadow: ${(props) => (props.soft ? "none" : "0 1px 2px rgba(0, 0, 0, 0.1)")};
`;

const EventTitle = styled.div`
  font: ${font.bold_12};
  font-size: 14px;
  color: #1a202c;
  line-height: 1.5;
  margin-bottom: 10px;
`;

const Section = styled.div`
  margin-top: 10px;

  &:first-child {
    margin-top: 0;
  }
`;

const SectionLabel = styled.div`
  font: ${font.bold_12};
  font-size: 12px;
  color: #2d3748;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 10px;
  font: ${font.normalMicro_16};
  font-size: 12px;
  color: #4a5568;
  line-height: 1.5;
`;

const InfoLabel = styled.span`
  font: ${font.bold_12};
  font-size: 12px;
  color: #2d3748;
  white-space: nowrap;
`;

const InfoValue = styled.span`
  color: #4a5568;
  font-size: 12px;
  word-break: break-word;
`;

const MetaGrid = styled(InfoGrid)`
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #f7fafc;
  border-radius: 6px;
  border: 1px solid #edf2f7;
`;

const CollapsedHint = styled.span`
  color: #a0aec0;
  font-weight: normal;
`;

const SequenceGroup = styled(EventCard)``;

const SequenceHeader = styled(EventCardHeader)``;

const SequenceBody = styled.div`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  padding: 0 10px 10px;
  border-top: 1px solid #edf2f7;
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  margin-top: 8px;
`;

const ScoreCard = styled.div`
  text-align: center;
  padding: 10px 8px;
  background: #f7fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const ScoreLabel = styled.div`
  font: ${font.medium_11};
  font-size: 9px;
  color: #718096;
  margin-bottom: 4px;
  text-transform: capitalize;
  letter-spacing: 0.3px;
`;

const ScoreValue = styled.div`
  font: ${font.bold_16};
  color: ${(props) => {
    const { value } = props;
    if (value >= 18) return "#10b981";
    if (value >= 15) return "#f59e0b";
    return "#ef4444";
  }};
  font-size: 18px;
`;

const TextBox = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  background: ${(props) => {
    if (props.variant === "reason") return "#fef3c7";
    if (props.variant === "improvement") return "#dbeafe";
    return "#f7fafc";
  }};
  border-left: 3px solid ${(props) => {
    if (props.variant === "reason") return "#f59e0b";
    if (props.variant === "improvement") return "#3b82f6";
    return "#cbd5e0";
  }};
  border-radius: 6px;
  font: ${font.normalMicro_16};
  font-size: 12px;
  color: #1a202c;
  line-height: 1.5;
`;

const WeaknessBox = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: #fee2e2;
  border-left: 3px solid #ef4444;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WeaknessLabel = styled.span`
  font: ${font.bold_12};
  font-size: 12px;
  color: #991b1b;
`;

const WeaknessValue = styled.span`
  font: ${font.normalMicro_16};
  font-size: 12px;
  color: #7f1d1d;
`;

const getEventVariant = (eventType) => {
  if (eventType === "agent_start" || eventType === "agent_end") return "agent";
  if (eventType === "iteration_start") return "iteration";
  if (eventType === "reasoning") return "reasoning";
  if (eventType === "initialize") return "initialize";
  if (eventType === "summary") return "summary";
  return null;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const formatDetailValue = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (typeof value[0] === "object") return `${value.length} items`;
    return value.join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const getEventPreview = (event) => {
  const { details } = event;
  if (!details) return null;

  if (typeof details.score === "number") {
    const parts = [`${details.score}/100`];
    if (details.short_id) parts.push(details.short_id);
    if (details.decision) parts.push(details.decision);
    return parts.join(" · ");
  }

  if (details.task) return details.task;
  if (details.reason) return details.reason;
  if (details.mode) return details.mode;
  if (details.total_shorts != null) return `${details.total_shorts} shorts generated`;
  if (details.count != null) return `${details.count} items`;
  if (details.shorts_to_review != null) return `${details.shorts_to_review} shorts to review`;

  return null;
};

const ExecutionTracePanel = ({ executionTrace, projectTitle, onClose, isDrawer = false }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const [expandedSequences, setExpandedSequences] = useState({});

  const events = executionTrace?.events || [];
  const { timeline } = useMemo(
    () => buildExecutionTraceTimeline(events),
    [events]
  );

  const sequenceNavItems = useMemo(() => {
    const seen = new Set();
    return timeline
      .filter((item) => item.type === "sequence")
      .filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
  }, [timeline]);

  const toggleCard = (eventKey) => {
    setExpandedCards((prev) => ({
      ...prev,
      [eventKey]: !prev[eventKey],
    }));
  };

  const toggleSequence = (seqId) => {
    setExpandedSequences((prev) => ({
      ...prev,
      [seqId]: !prev[seqId],
    }));
  };

  const scrollToTimelineBlock = (blockId) => {
    setExpandedSequences((prev) => ({ ...prev, [blockId]: true }));
    const element = document.getElementById(blockId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const expandAllCards = () => {
    const nextCards = {};
    events.forEach((event, index) => {
      const key = event.step_id || `event-${index}`;
      nextCards[key] = true;
    });
    const nextSequences = {};
    timeline.forEach((item) => {
      if (item.type === "sequence") nextSequences[item.id] = true;
    });
    setExpandedCards(nextCards);
    setExpandedSequences(nextSequences);
  };

  const collapseAllCards = () => {
    setExpandedCards({});
    setExpandedSequences({});
  };

  const renderEventMeta = (event) => {
    const metaFields = [
      ["step id", event.step_id],
      ["time", formatTimestamp(event.timestamp)],
      ["parent id", event.parent_id],
      ["agent id", event.agent_id],
      ["iteration", event.iteration],
      ["duration", event.duration_ms != null ? `${event.duration_ms} ms` : null],
    ].filter(([, value]) => value != null && value !== "");

    if (metaFields.length === 0) return null;

    return (
      <MetaGrid>
        {metaFields.map(([label, value]) => (
          <React.Fragment key={label}>
            <InfoLabel>{label}:</InfoLabel>
            <InfoValue>{String(value)}</InfoValue>
          </React.Fragment>
        ))}
      </MetaGrid>
    );
  };

  const renderEventDetails = (event) => {
    const { details } = event;
    if (!details || typeof details !== "object") return null;

    const simpleFields = [];
    const excludedKeys = ["breakdown", "reason", "improvement", "improvement_plan", "weakest_factor", "details"];

    Object.entries(details).forEach(([key, value]) => {
      if (excludedKeys.includes(key)) return;
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        simpleFields.push(
          <React.Fragment key={key}>
            <InfoLabel>{key.replace(/_/g, " ")}:</InfoLabel>
            <InfoValue>{String(value)}</InfoValue>
          </React.Fragment>
        );
        return;
      }
      if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
        simpleFields.push(
          <React.Fragment key={key}>
            <InfoLabel>{key.replace(/_/g, " ")}:</InfoLabel>
            <InfoValue as="pre" style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>
              {formatDetailValue(value)}
            </InfoValue>
          </React.Fragment>
        );
      }
    });

    if (details.details && typeof details.details === "object") {
      simpleFields.push(
        <React.Fragment key="nested-details">
          <InfoLabel>decision details:</InfoLabel>
          <InfoValue as="pre" style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>
            {formatDetailValue(details.details)}
          </InfoValue>
        </React.Fragment>
      );
    }

    return (
      <>
        {renderEventMeta(event)}

        {simpleFields.length > 0 && (
          <Section>
            <InfoGrid>{simpleFields}</InfoGrid>
          </Section>
        )}

        {details.weakest_factor && (
          <Section>
            <WeaknessBox>
              <WeaknessLabel>⚠ Weakest:</WeaknessLabel>
              <WeaknessValue>{details.weakest_factor}</WeaknessValue>
            </WeaknessBox>
          </Section>
        )}

        {details.breakdown && (
          <Section>
            <SectionLabel>📊 Score Breakdown</SectionLabel>
            <ScoreGrid>
              {Object.entries(details.breakdown).map(([key, value]) => (
                <ScoreCard key={key}>
                  <ScoreLabel>{key.replace(/_/g, " ")}</ScoreLabel>
                  <ScoreValue value={value}>{value}/{BREAKDOWN_SCORE[key]}</ScoreValue>
                </ScoreCard>
              ))}
            </ScoreGrid>
          </Section>
        )}

        {details.reason && (
          <Section>
            <SectionLabel>💭 Reason</SectionLabel>
            <TextBox variant="reason">{details.reason}</TextBox>
          </Section>
        )}

        {(details.improvement || details.improvement_plan) && (
          <Section>
            <SectionLabel>💡 Improvement</SectionLabel>
            <TextBox variant="improvement">
              {details.improvement || details.improvement_plan}
            </TextBox>
          </Section>
        )}
      </>
    );
  };

  const renderEventCard = (event, eventKey) => {
    const variant = getEventVariant(event.event_type);
    const isExpanded = expandedCards[eventKey] === true;
    const preview = getEventPreview(event);
    const title = event.title || event.step_id;
    const summaryTitle = preview ? `${title} · ${preview}` : title;

    return (
      <EventCard key={eventKey} isExpanded={isExpanded}>
        <EventCardHeader
          type="button"
          isExpanded={isExpanded}
          onClick={() => toggleCard(eventKey)}
          aria-expanded={isExpanded}
        >
          <Pill variant={variant} soft>{event.event_type}</Pill>
          <CollapsedTitle title={summaryTitle}>
            {title}
            {preview && !isExpanded && (
              <CollapsedHint>{` · ${preview}`}</CollapsedHint>
            )}
          </CollapsedTitle>
          <CardChevron isExpanded={isExpanded}>▶</CardChevron>
        </EventCardHeader>

        {isExpanded && (
          <EventCardBody>
            <EventHeaderRow>
              <Pill variant={variant}>{event.event_type}</Pill>
              {event.iteration && <Pill variant="iteration">iteration {event.iteration}</Pill>}
            </EventHeaderRow>
            <EventTitle>{title}</EventTitle>
            {renderEventDetails(event)}
          </EventCardBody>
        )}
      </EventCard>
    );
  };

  const renderSequenceBlock = (sequenceBlock) => {
    const summary = getSequenceBlockSummary(sequenceBlock);
    const isOpen = !!expandedSequences[sequenceBlock.id];
    const blockLabel = sequenceBlock.iteration != null
      ? `${sequenceBlock.seqId} · iteration ${sequenceBlock.iteration}`
      : sequenceBlock.seqId;
    const scoreLabel = summary.lastScore != null
      ? `${summary.lastScore}/100`
      : `${summary.eventCount} events`;

    return (
      <SequenceGroup key={sequenceBlock.id} id={sequenceBlock.id} isExpanded={isOpen}>
        <SequenceHeader
          type="button"
          isExpanded={isOpen}
          onClick={() => toggleSequence(sequenceBlock.id)}
          aria-expanded={isOpen}
        >
          <Pill variant="iteration" soft>sequence</Pill>
          <CollapsedTitle title={`${blockLabel} · ${scoreLabel}`}>
            {blockLabel.replace("iteration 1", "Reviewing...")}
            <CollapsedHint>{` · ${scoreLabel}`}</CollapsedHint>
          </CollapsedTitle>
          <CardChevron isExpanded={isOpen}>▶</CardChevron>
        </SequenceHeader>
        <SequenceBody isOpen={isOpen}>
          {sequenceBlock.events.map((event) => {
            const eventKey = event.step_id || `${sequenceBlock.id}-${event.timestamp}`;
            return renderEventCard(event, eventKey);
          })}
        </SequenceBody>
      </SequenceGroup>
    );
  };

  const renderTimelineItem = (item, index) => {
    if (item.type === "event") {
      const eventKey = item.event.step_id || `timeline-event-${index}`;
      return renderEventCard(item.event, eventKey);
    }
    return renderSequenceBlock(item);
  };

  return (
    <Panel isDrawer={isDrawer}>
      <Header>
        <HeaderText>
          <HeaderTitle>Agent Review</HeaderTitle>
          <ProjectTitle>Execution trace log</ProjectTitle>
          {projectTitle && <ProjectTitle>{projectTitle}</ProjectTitle>}
        </HeaderText>
        {onClose && (
          <CloseButton type="button" onClick={onClose} aria-label="Close Agent Review">
            ×
          </CloseButton>
        )}
      </Header>

      {events.length > 0 && (
        <TraceToolbar>
          <ToolbarButton type="button" onClick={expandAllCards}>Expand all</ToolbarButton>
          <ToolbarButton type="button" onClick={collapseAllCards}>Collapse all</ToolbarButton>
        </TraceToolbar>
      )}

      {sequenceNavItems.length > 0 && (
        <QuickNav>
          <QuickNavLabel>Jump to sequence</QuickNavLabel>
          {sequenceNavItems.map((item) => {
            const summary = getSequenceBlockSummary(item);
            const label = item.iteration != null
              ? `${item.seqId} (iter ${item.iteration})`
              : item.seqId;
            return (
              <QuickNavPill
                key={item.id}
                type="button"
                onClick={() => scrollToTimelineBlock(item.id)}
              >
                {label}
                {summary.lastScore != null ? ` · ${summary.lastScore}` : ""}
              </QuickNavPill>
            );
          })}
        </QuickNav>
      )}

      <Content>
        {timeline.map((item, index) => renderTimelineItem(item, index))}
      </Content>
    </Panel>
  );
};

ExecutionTracePanel.propTypes = {
  executionTrace: PropTypes.object.isRequired,
  projectTitle: PropTypes.string,
  onClose: PropTypes.func,
  isDrawer: PropTypes.bool,
};

export default ExecutionTracePanel;
